import type { Theme } from "@mui/material/styles";
import type { MenuProps } from "@mui/material/Menu";

import { PUCK_CONFIG } from "../constants";

export type EditorSizeOptions = {
    minHeight?: number;
    maxHeight?: number;
};

export const menuBarSx = (theme: Theme) => ({
    padding: theme.spacing(0.5),
    border: `1px solid ${theme.palette.divider}`,
    borderBottom: "none",
    borderTopLeftRadius: theme.shape.borderRadius,
    borderTopRightRadius: theme.shape.borderRadius,
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-start",
    flexWrap: "wrap",
    gap: theme.spacing(0.25),
    backgroundColor: theme.palette.background.paper,
});

export const menuButtonSx = (theme: Theme, isActive?: boolean) => ({
    background: isActive ? `${theme.palette.primary[50]}` : "transparent",
    border: "none",
    borderRadius: theme.spacing(0.5),
    fontFamily: PUCK_CONFIG.FontFamily,
    height: theme.spacing(4),
    width: theme.spacing(4),
    margin: 0,
    padding: 0,
    minWidth: "auto",
    textDecoration: "none",
    color: isActive ? theme.palette.primary.main : "inherit",
    "&:hover": {
        backgroundColor: theme.palette.action.hover,
        textDecoration: "none",
    },
    "&.Mui-focusVisible": {
        outline: `2px solid ${theme.palette.primary.light}`,
    },
    "& .MuiSvgIcon-root": {
        fontSize: "1.25rem",
    },
    "& .MuiTypography-root": {
        color: isActive ? theme.palette.primary.main : "inherit",
    },
});

export const dropdownSx = (theme: Theme) => ({
    height: theme.spacing(4),
    mx: 0.25,
    backgroundColor: theme.palette.action.hover,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.spacing(0.5),
    transition: theme.transitions.create(["background-color", "border-color"], {
        duration: theme.transitions.duration.shortest,
    }),
    "&:hover": {
        backgroundColor: theme.palette.action.selected,
    },
    "&.Mui-focused": {
        borderColor: theme.palette.primary.main,
    },
    "&::before, &::after": {
        display: "none",
    },
    "& .MuiSelect-select": {
        padding: `${theme.spacing(0.25)} ${theme.spacing(3)} ${theme.spacing(0.25)} ${theme.spacing(1)}`,
        fontSize: theme.typography.body2.fontSize,
        fontFamily: PUCK_CONFIG.FontFamily,
        minHeight: "0 !important",
        lineHeight: 1.4,
        display: "flex",
        alignItems: "center",
    },
    "& .MuiSelect-icon": {
        right: theme.spacing(0.25),
        fontSize: "1.1rem",
    },
});

export const dropdownMenuProps: Partial<MenuProps> = {
    elevation: 4,
    slotProps: {
        paper: {
            sx: (theme: Theme) => ({
                mt: 0.5,
                borderRadius: theme.spacing(0.5),
                "& .MuiList-root": {
                    py: 0.5,
                },
                "& .MuiMenuItem-root": {
                    fontSize: theme.typography.body2.fontSize,
                    fontFamily: PUCK_CONFIG.FontFamily,
                    minHeight: "auto",
                    py: 0.5,
                    px: 1.5,
                    margin: 0,
                    borderRadius: 0,
                    lineHeight: 1.4,
                    "&.Mui-selected": {
                        backgroundColor: theme.palette.action.selected,
                        margin: 0,
                        borderRadius: 0,
                        color: theme.palette.text.primary,
                    },
                    "&.Mui-selected:hover": {
                        backgroundColor: theme.palette.action.selected,
                        borderRadius: 0,
                    },
                    "&:hover": {
                        backgroundColor: theme.palette.action.hover,
                        borderRadius: 0,
                    },
                },
            }),
        },
    },
};

// Puck-native header strip for the link bar — matches NativePuckTitleField's
// label color/font + a subtle bottom border separator.
export const linkBarHeaderSx = {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    padding: "8px 12px",
    borderBottom: "1px solid var(--puck-color-grey-09)",
    backgroundColor: "var(--puck-color-grey-12)",
    fontFamily: "var(--puck-font-family)",
} as const;

export const linkBarHeaderTitleSx = {
    fontSize: "13px",
    fontWeight: 600,
    color: "var(--puck-color-grey-04)",
    fontFamily: "var(--puck-font-family)",
} as const;

// Container-query driven layout. The PARENT (`editorContainerSx` below)
// declares itself as a named query container `rich-text-editor` via the
// `container` shorthand. This `linkBarSx` is a CHILD of that container, so its
// `@container rich-text-editor (...)` rules query the editor container's
// width — independent of viewport size, independent of the link bar's own
// width.
//
// Default layout = vertical stack (Text on top, URL below, Actions row last)
// because most CMS authoring contexts are < 900 px wide and the user wants
// "compressed" containers to stack inputs.
//
// At >= 900 px the bar collapses to a single horizontal row. 900 px is wider
// than typical Puck sidebar / settings panel widths (~280–800 px) so the
// horizontal mode only kicks in for wide preview/full-width contexts.
export const linkBarSx = (theme: Theme) => ({
    display: "grid",
    gap: theme.spacing(1),
    alignItems: "center",
    padding: theme.spacing(1, 1.5),
    borderBottom: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.action.hover,
    gridTemplateColumns: "1fr",
    gridTemplateAreas: `
        "text"
        "url"
        "actions"
    `,
    "@container rich-text-editor (min-width: 900px)": {
        gridTemplateColumns: "minmax(120px, 1fr) minmax(160px, 2fr) auto",
        gridTemplateAreas: `"text url actions"`,
    },
    "& .MuiTextField-root .MuiInputBase-root": {
        backgroundColor: theme.palette.background.paper,
    },
    "& .MuiTextField-root .MuiInputBase-input": {
        fontSize: "0.8125rem",
    },
});

// Puck-native input — mirrors the styling pattern from MediaManagerField.tsx
// and TokenizedTextField.tsx so the link bar's Text/URL inputs read as
// first-party Puck fields instead of MUI TextFields. Uses `--puck-*` CSS
// variables exclusively (no theme primary, which would render red in
// foodpilot's theme — Puck uses azure for focus rings).
export const nativePuckInputSx = {
    width: "100%",
    padding: "8px 12px",
    border: "1px solid var(--puck-color-grey-08)",
    borderRadius: "4px",
    fontSize: "14px",
    fontFamily: "var(--puck-font-family)",
    backgroundColor: "var(--puck-color-white)",
    color: "var(--puck-color-black)",
    outline: "none",
    boxSizing: "border-box" as const,
    transition: "border-color 50ms ease-in, box-shadow 50ms ease-in",
    "&:hover": {
        borderColor: "var(--puck-color-grey-05)",
    },
    "&:focus": {
        borderColor: "var(--puck-color-azure-04)",
        boxShadow: "0 0 0 2px var(--puck-color-azure-09)",
    },
    "&::placeholder": {
        color: "var(--puck-color-grey-05)",
    },
} as const;

// Puck-native field label — matches NativePuckTitleField's label color/font.
export const nativePuckLabelSx = {
    display: "block",
    marginBottom: "4px",
    fontSize: "12px",
    fontWeight: 600,
    fontFamily: "var(--puck-font-family)",
    color: "var(--puck-color-grey-05)",
} as const;

// Puck-native action button (used for Apply, Cancel, Remove). Flat, white,
// thin grey border, square corners. The variant prop tweaks the text color
// only — keeping a unified shape across the row so they read as a button
// group, not three different MUI styles.
type LinkBarButtonVariant = "primary" | "secondary" | "danger";
export const linkBarButtonSx = (variant: LinkBarButtonVariant) =>
    ({
        height: "32px",
        minWidth: 64,
        padding: "0 12px",
        fontSize: "13px",
        fontWeight: 600,
        fontFamily: "var(--puck-font-family)",
        textTransform: "none" as const,
        textDecoration: "none",
        borderRadius: "4px",
        backgroundColor: "var(--puck-color-white)",
        border: "1px solid var(--puck-color-grey-08)",
        boxShadow: "none",
        cursor: "pointer",
        color:
            variant === "primary" ? "var(--puck-color-azure-04)"
            : variant === "danger" ? "var(--puck-color-red-04)"
            : "var(--puck-color-grey-04)",
        "&:hover": {
            boxShadow: "none",
            backgroundColor: "var(--puck-color-grey-12)",
            borderColor: "var(--puck-color-grey-05)",
            textDecoration: "none",
        },
        "&.Mui-disabled": {
            color: "var(--puck-color-grey-08)",
            borderColor: "var(--puck-color-grey-09)",
            backgroundColor: "var(--puck-color-white)",
            cursor: "not-allowed",
        },
    }) as const;

export const editorContainerSx = (theme: Theme, options?: EditorSizeOptions) => ({
    // Named container for the link bar's @container queries.
    // Verified via Context7 (CSS Almanac, /websites/css-tricks_almanac):
    // `container: <name> / inline-size` is the shorthand for
    // `container-name` + `container-type`.
    container: "rich-text-editor / inline-size",
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: "0 0 4px 4px",
    overflow: "hidden",
    "& .ProseMirror": {
        minHeight: options?.minHeight ?? 150,
        maxHeight: options?.maxHeight ?? 400,
        overflowY: "auto",
        padding: theme.spacing(1.5),
        outline: "none",
        overflowWrap: "anywhere",
        wordBreak: "break-word",
        fontFamily: theme.typography.fontFamily,
        fontSize: theme.typography.fontSize,
        "& p": {
            margin: `${theme.spacing(0.5)} 0`,
        },
        "& h1": {
            ...theme.typography.h1,
            margin: `${theme.spacing(1)} 0`,
        },
        "& h2": {
            ...theme.typography.h2,
            margin: `${theme.spacing(1)} 0`,
        },
        "& h3": {
            ...theme.typography.h3,
            margin: `${theme.spacing(1)} 0`,
        },
        "& h4": {
            ...theme.typography.h4,
            margin: `${theme.spacing(1)} 0`,
        },
        "&:focus": {
            outline: "none",
        },
        "& ul, & ol": {
            paddingLeft: `${theme.spacing(1.5)}`,
        },
        "& a": {
            color: theme.palette.primary.main,
            textDecoration: "underline",
        },
        "& img.editor-image": {
            maxWidth: "100%",
            height: "auto",
            display: "block",
            borderRadius: theme.shape.borderRadius,
            margin: "1em auto",
            boxShadow: theme.shadows[1],
        },
        '& [style*="font-size: small"]': {
            fontSize: `${theme.typography.fontSize * 0.75}px`,
        },
        '& [style*="font-size: medium"]': {
            fontSize: `${theme.typography.fontSize}px`,
        },
        '& [style*="font-size: large"]': {
            fontSize: `${theme.typography.fontSize * 1.5}px`,
        },
        "&.ProseMirror-focused": {
            boxShadow: `0 0 0 2px ${theme.palette.primary.light}1a`,
        },
    },
});
